/**
 * AetherPage — Shared dark-theme page shell
 * Wraps: Sidebar + sticky glass header + main content area + MobileNav
 *
 * Props:
 *   title          string          — Page heading text
 *   icon           LucideIcon      — Icon shown left of title on desktop
 *   iconColor      string          — Hex color for the icon (default: '#3B82F6')
 *   titleTestId    string          — data-testid on the <h1>
 *   subtitle       string          — Small muted subtitle below the title
 *   badge          ReactNode       — Slot after title (count badge, LIVE dot, etc.)
 *   rightElement   ReactNode       — Slot on the right side of the header
 *   headerExtra    ReactNode       — Full-width slot below title row (tab bar, search bar, etc.)
 *   mainClassName  string          — Override default main className
 *   children       ReactNode       — Page content rendered inside <main>
 */
import React from 'react';
import Sidebar from './Sidebar';
import MobileNav from './MobileNav';

export const AetherPage = ({
  title,
  icon: Icon,
  iconColor = '#3B82F6',
  titleTestId,
  subtitle,
  badge,
  rightElement,
  headerExtra,
  mainClassName,
  children,
}) => (
  <div className="min-h-screen" style={{ background: 'var(--aether-bg)' }}>
    <Sidebar />

    <div className="lg:ml-64 pb-20 lg:pb-8">
      {/* ── Sticky Glass Header ── */}
      <header
        className="sticky top-0 z-30 border-b border-[#262626]"
        style={{ background: 'rgba(19,19,19,0.78)', backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)' }}
      >
        <div className="px-4 md:px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            {Icon && (
              <Icon
                className="h-5 w-5 hidden lg:block shrink-0"
                style={{ color: iconColor }}
              />
            )}
            <div className="min-w-0">
              <h1 className="headline-md text-[#F4F4F5] truncate" data-testid={titleTestId}>
                {title}
              </h1>
              {subtitle && (
                <p className="text-xs text-[#8A8A93] mt-0.5 hidden md:block">{subtitle}</p>
              )}
            </div>
            {badge && <div className="shrink-0">{badge}</div>}
          </div>
          {rightElement && <div className="shrink-0">{rightElement}</div>}
        </div>
        {headerExtra}
      </header>

      {/* ── Page content ── */}
      <main className={mainClassName ?? 'p-4 md:p-6 space-y-6'}>
        {children}
      </main>
    </div>

    <MobileNav />
  </div>
);

export default AetherPage;
